import React, { useState, useCallback } from "react";
import useSWR from "swr";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, Spinner, Empty } from "@/components/Atoms";
import {
    Activity, RefreshCw, AlertTriangle, Wrench,
    ChevronDown, ChevronRight, Server, Shield, Zap,
    Mic, FileText, Boxes, ArrowUpCircle, CheckCircle2,
    Loader2,
} from "lucide-react";
import {
    HealthBadge, HEALTH_META, HealthTable,
    SouveraHealthTable, PmgHealthTable,
} from "@/components/ServiceHealth";

const fetcher = (u) => api.get(u).then((r) => r.data);
const m = (h) => HEALTH_META[h] || HEALTH_META.unknown;
const fmtAgo = (iso) => {
    if (!iso) return "—";
    const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${Math.round(s)}s ago`;
    if (s < 3600) return `${Math.round(s / 60)}m ago`;
    return `${Math.round(s / 3600)}h ago`;
};
const fmtLat = (ms) => (ms == null ? "—" : ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${ms}ms`);
const isProblem = (h) => ["down", "critical", "degraded", "unreachable"].includes(h);
const severity = (h) => ({ down: 0, unreachable: 1, critical: 2, degraded: 3 }[h] ?? 9);
const UPDATABLE_KINDS = new Set(["talk-signaling", "collabora", "nextcloud-collabora"]);

function mergeCounts(objs) {
    const out = {};
    for (const obj of objs) for (const [k, v] of Object.entries(obj)) out[k] = (out[k] || 0) + v;
    return out;
}
function CountPills({ counts }) {
    const order = ["down", "unreachable", "critical", "degraded", "pending", "healthy", "unknown"];
    return (
        <div className="flex flex-wrap gap-1.5">
            {order.filter((k) => counts?.[k]).map((k) => (
                <span key={k} className={`px-1.5 py-0.5 border rounded-sm text-[11px] font-mono ${m(k).cls}`}>
                    {m(k).label}: {counts[k]}
                </span>
            ))}
        </div>
    );
}

function CollapseBtn({ open, onClick, label }) {
    return (
        <button onClick={onClick}
            className="flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-300 font-mono uppercase tracking-wider">
            {open ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
            {label}
        </button>
    );
}

// Group service VMs by kind for the infrastructure overview cards.
const INFRA_KINDS = [
    { kind: "collabora", icon: FileText, label: "Collabora" },
    { kind: "nextcloud-collabora", icon: FileText, label: "Dedicated Collabora" },
    { kind: "nextcloud-eurooffice", icon: FileText, label: "Euro-Office DS" },
    { kind: "talk-signaling", icon: Mic, label: "Talk HPB" },
    { kind: "notify_push", icon: Zap, label: "Notify Push" },
    { kind: "caddy", icon: Shield, label: "Caddy LB" },
    { kind: "egress-gw", icon: Server, label: "Egress Gateway" },
    { kind: "coturn", icon: Mic, label: "coTURN" },
    { kind: "redis", icon: Activity, label: "Redis" },
    { kind: "redis-lb", icon: Activity, label: "Redis LB" },
    { kind: "nextcloud-redis", icon: Activity, label: "NC Redis" },
];

function InfraCard({ kind, icon: Icon, entries, onUpdate }) {
    if (!entries.length) return null;
    const worst = entries.sort((a, b) => severity(a.health) - severity(b.health))[0];
    const healthyCount = entries.filter((e) => e.health === "healthy").length;
    const totalCount = entries.length;
    const hm = m(worst.health);
    const first = entries[0];
    const ver = first.running_version;
    const outdated = first.version_outdated;
    const upKind = entries.find(e => UPDATABLE_KINDS.has(e.kind));
    // Eindeutige Cloud-Zuordnung anzeigen (nur wenn ALLE VMs der Karte
    // zu derselben Cloud gehören — geteilte Infra bleibt ohne Hinweis).
    const cloudNames = [...new Set(entries.map((e) => e.cloud_name).filter(Boolean))];
    const singleCloud = cloudNames.length === 1 ? cloudNames[0] : null;
    return (
        <div className="cm-card p-3 border border-zinc-800 min-w-[160px]" data-testid={`health-infra-${kind}`}>
            <div className="flex items-center gap-1.5 mb-2">
                <Icon size={12} className={worst.health === "healthy" ? "text-emerald-400" : "text-red-400"} />
                <span className="text-[10px] font-mono text-zinc-300 uppercase tracking-wider">{kind}</span>
            </div>
            {singleCloud && (
                <div className="mb-2 text-[9px] text-zinc-500 font-mono truncate" title={`Cloud: ${singleCloud}`}>
                    {singleCloud}
                </div>
            )}
            <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${hm.dot}`} />
                <span className={`text-[11px] font-mono ${worst.health === "healthy" ? "text-emerald-300" : hm.cls.split(" ")[0]}`}>
                    {hm.label}
                </span>
                <span className="text-[10px] text-zinc-500">{healthyCount}/{totalCount}</span>
            </div>
            {ver && (
                <div className="mt-1 text-[10px] text-zinc-500 font-mono flex items-center gap-1">
                    v{ver}
                    {outdated && (
                        <span className="text-amber-400 flex items-center gap-0.5">
                            <AlertTriangle size={9} /> outdated
                        </span>
                    )}
                </div>
            )}
            {worst.health !== "healthy" && worst.detail && (
                <div className="mt-1.5 text-[10px] text-zinc-500 font-mono line-clamp-2">{worst.detail}</div>
            )}
            {worst.latency_ms != null && (
                <div className="mt-1 text-[10px] text-zinc-600 font-mono">Latenz: {fmtLat(worst.latency_ms)}</div>
            )}
            {upKind && outdated && onUpdate && (
                <button onClick={() => onUpdate(upKind.id)} data-testid={`health-update-${kind}`}
                    className="mt-2 px-2 py-0.5 text-[10px] font-mono uppercase border border-amber-700/50 text-amber-400 hover:bg-amber-500/10 flex items-center gap-1">
                    <ArrowUpCircle size={10} /> Update
                </button>
            )}
        </div>
    );
}

function InfraGrid({ services, onUpdate }) {
    const byKind = {};
    for (const s of (services || [])) {
        if (!byKind[s.kind]) byKind[s.kind] = [];
        byKind[s.kind].push(s);
    }
    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {INFRA_KINDS.map(({ kind, icon, label }) => (
                <InfraCard key={kind} kind={label} icon={icon} entries={byKind[kind] || []} onUpdate={onUpdate} />
            ))}
        </div>
    );
}

export default function HealthPage() {
    const { data, error, mutate } = useSWR("/api/admin/infra/health", fetcher, { refreshInterval: 15000 });
    const { data: alerts } = useSWR("/api/admin/infra/monitoring/alerts?limit=30", fetcher, { refreshInterval: 30000 });
    const { data: openAlerts, mutate: mutOpen } = useSWR("/api/admin/infra/monitoring/open-alerts", fetcher, { refreshInterval: 15000 });

    const ackAlert = async (entityId) => {
        try {
            await api.post(`/api/admin/infra/monitoring/alerts/${encodeURIComponent(entityId)}/ack`, { by: "operator" });
            mutOpen();
        } catch (e) { /* noop */ }
    };

    const [showServices, setShowServices] = useState(false);
    const [showSouvera, setShowSouvera] = useState(false);
    const [showClouds, setShowClouds] = useState(false);
    const [updating, setUpdating] = useState({});

    const doUpdate = useCallback(async (svcId) => {
        setUpdating((p) => ({ ...p, [svcId]: true }));
        try {
            const r = await api.post(`/api/admin/infra/service-vms/${svcId}/update-version`);
            const d = r.data;
            if (d.ok) {
                setUpdating((p) => ({ ...p, [svcId]: "done" }));
                setTimeout(() => mutate(), 2000);
            } else {
                setUpdating((p) => ({ ...p, [svcId]: `error: ${d.output_tail?.slice(-80) || "unknown"}` }));
            }
        } catch (e) {
            setUpdating((p) => ({ ...p, [svcId]: `error: ${e.response?.data?.detail || e.message}` }));
        }
    }, [mutate]);

    if (error) return <Empty title="Health could not be loaded" />;
    if (!data) return <div className="p-12 flex justify-center"><Spinner /></div>;

    const all = [...(data.services || []), ...(data.clouds || []), ...(data.souvera || []), ...(data.pmg || [])];
    const problems = all.filter((e) => isProblem(e.health)).sort((a, b) => severity(a.health) - severity(b.health));
    const merged = mergeCounts([
        data.summary?.services || {}, data.summary?.clouds || {},
        data.summary?.souvera || {}, data.summary?.pmg || {},
    ]);
    const overall = data.summary?.overall;
    const healthyAll = Object.entries(merged).filter(([k]) => !isProblem(k)).reduce((s, [, v]) => s + v, 0);

    return (
        <div data-testid="health-page" className="space-y-4">
            {Array.isArray(openAlerts) && openAlerts.length > 0 && (
                <div className="cm-card p-3 border-red-900/60" data-testid="open-alerts-banner">
                    <div className="flex flex-wrap gap-2 items-center">
                        <span className="text-xs font-mono uppercase tracking-widest text-red-300">Open alerts:</span>
                        {openAlerts.map((a) => (
                            <span key={a.entity_id} className="flex items-center gap-1.5 text-xs font-mono text-zinc-200 bg-zinc-900 border border-zinc-800 px-2 py-1">
                                {a.name} · {a.severity}
                                {!a.acknowledged && (
                                    <button data-testid={`banner-ack-${a.entity_id}`}
                                        onClick={() => ackAlert(a.entity_id)}
                                        className="text-[#CCFF00] hover:underline ml-1">acknowledge</button>
                                )}
                            </span>
                        ))}
                        <Link to="/alerts" className="ml-auto text-xs font-mono text-[#CCFF00] uppercase tracking-wider">Alert Center →</Link>
                    </div>
                </div>
            )}
            <PageHeader
                testid="health-header"
                title="Infrastructure Health"
                subtitle={`Last probe: ${fmtAgo(data.generated_at)} — ${healthyAll} healthy, ${problems.length} problems`}
                action={
                    <button onClick={() => mutate()} data-testid="health-refresh"
                        className="px-3 py-2 text-xs font-mono uppercase tracking-widest border border-zinc-800 text-zinc-300 hover:border-[#CCFF00] flex items-center gap-2">
                        <RefreshCw size={14} /> Refresh
                    </button>
                }
            />

            {/* Summary */}
            <div className="cm-card p-4 border border-zinc-800">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                    <Activity size={16} className="text-[#CCFF00]" />
                    <HealthBadge health={overall} testid="health-overall" />
                    <span className="text-xs text-zinc-500 font-mono">
                        {data.services?.length || 0} Services · {data.clouds?.length || 0} Clouds · {data.souvera?.length || 0} Souvera · {data.pmg?.length || 0} PMG
                    </span>
                </div>
                <CountPills counts={merged} />
            </div>

            {/* ACTIVE PROBLEMS — always visible */}
            {problems.length > 0 && (
                <div className="cm-card border border-red-900/50" data-testid="health-problems">
                    <div className="px-3 py-2 text-xs uppercase tracking-widest text-red-400 font-mono border-b border-red-900/30 flex items-center gap-2">
                        <AlertTriangle size={12} /> Problems ({problems.length})
                    </div>
                    <div className="divide-y divide-zinc-900">
                        {problems.map((e) => (
                            <div key={e.id} className="px-3 py-2 text-xs flex flex-wrap items-center gap-x-3 gap-y-1 font-mono">
                                <span className="text-zinc-600 text-[11px]">{(e.category || e.kind || "").toUpperCase()}</span>
                                <span className="text-zinc-300">{e.name}</span>
                                {e.cloud_name && (
                                    <span className="text-zinc-500 text-[11px]" title={`Cloud ${e.cloud_name}`}>
                                        → {e.cloud_name}
                                    </span>
                                )}
                                <HealthBadge health={e.health} />
                                {e.latency_ms != null && <span className="text-zinc-500">{fmtLat(e.latency_ms)}</span>}
                                <span className="text-zinc-500">{fmtAgo(e.last_check)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {problems.length === 0 && (
                <div className="cm-card p-4 border border-emerald-900/50 text-xs text-emerald-300/80 flex items-center gap-2 font-mono" data-testid="health-allgood">
                    <span className="w-2 h-2 rounded-full bg-emerald-400" />
                    All systems healthy — no problems.
                </div>
            )}

            {/* PMG — always visible */}
            <PmgHealthTable rows={data.pmg || []} />

            {/* Intensive Infrastructure Cards */}
            <div className="cm-card p-0 border border-zinc-800" data-testid="health-infra">
                <div className="px-3 py-2 text-xs uppercase tracking-widest text-zinc-500 font-mono border-b border-zinc-800 flex items-center gap-2">
                    <Server size={12} /> Core Infrastructure
                </div>
                <div className="p-3">
                    <InfraGrid services={data.services} onUpdate={doUpdate} />
                </div>
            </div>

            {/* Outdated VMs — only shown when there are outdated ones */}
            {(data.services || []).filter(e => e.version_outdated).length > 0 && (
                <div className="cm-card p-0 border border-amber-900/50" data-testid="health-outdated">
                    <div className="px-3 py-2 text-xs uppercase tracking-widest text-amber-400 font-mono border-b border-amber-900/30 flex items-center gap-2">
                        <ArrowUpCircle size={12} /> Updates available
                    </div>
                    <div className="divide-y divide-zinc-900">
                        {(data.services || []).filter(e => e.version_outdated).sort((a, b) => severity(a.health) - severity(b.health)).map((e) => (
                            <div key={e.id} className="px-3 py-2 text-xs flex flex-wrap items-center gap-x-3 gap-y-1 font-mono">
                                <span className="text-zinc-400 uppercase text-[11px]">{e.kind}</span>
                                <span className="text-zinc-300">{e.name}</span>
                                <span className="text-zinc-500">v{e.running_version}</span>
                                <span className="text-zinc-700">→</span>
                                <span className="text-emerald-400/80">v{typeof data.summary?.["version_targets"]?.[e.kind] === "string" ? data.summary?.["version_targets"]?.[e.kind] : "?"}</span>
                                <HealthBadge health={e.health} />
                                {updating[e.id] === "done" ? (
                                    <span className="text-emerald-400 flex items-center gap-1"><CheckCircle2 size={10} /> Updated</span>
                                ) : updating[e.id] === true ? (
                                    <span className="text-zinc-500 flex items-center gap-1"><Loader2 size={10} className="animate-spin" /> Updating...</span>
                                ) : updating[e.id] ? (
                                    <span className="text-red-400 text-[10px]">{updating[e.id]}</span>
                                ) : (
                                    <button onClick={() => doUpdate(e.id)} data-testid={`health-update-${e.kind}-${e.id}`}
                                        className="px-2 py-0.5 text-[10px] font-mono uppercase border border-amber-700/50 text-amber-400 hover:bg-amber-500/10 flex items-center gap-1">
                                        <ArrowUpCircle size={10} /> Update
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* All Service-VMs — collapsed by default, expand on demand */}
            <div className="cm-card p-0 border border-zinc-800" data-testid="health-services-section">
                <div className="px-3 py-2 text-xs uppercase tracking-widest text-zinc-500 font-mono border-b border-zinc-800 flex items-center justify-between">
                    <span>All service VMs ({(data.services || []).length})</span>
                    <CollapseBtn open={showServices} onClick={() => setShowServices(!showServices)}
                        label={showServices ? "Collapse" : `Expand (${(data.services || []).filter((e) => !isProblem(e.health)).length} healthy)`} />
                </div>
                {showServices && (
                    <HealthTable title="" rows={data.services || []} testid="health-services" showLat showCloud />
                )}
            </div>

            {/* Souvera Apps — collapsed */}
            {(data.souvera || []).length > 0 && (
                <div className="cm-card p-0 border border-zinc-800">
                    <div className="px-3 py-2 text-xs uppercase tracking-widest text-zinc-500 font-mono border-b border-zinc-800 flex items-center justify-between">
                        <span>Souvera Apps ({(data.souvera || []).length})</span>
                        <CollapseBtn open={showSouvera} onClick={() => setShowSouvera(!showSouvera)}
                            label={showSouvera ? "Collapse" : `Expand (${(data.souvera || []).filter((e) => !isProblem(e.health)).length} healthy)`} />
                    </div>
                    {showSouvera && <SouveraHealthTable rows={data.souvera || []} />}
                </div>
            )}

            {/* Clouds — collapsed */}
            {(data.clouds || []).length > 0 && (
                <div className="cm-card p-0 border border-zinc-800">
                    <div className="px-3 py-2 text-xs uppercase tracking-widest text-zinc-500 font-mono border-b border-zinc-800 flex items-center justify-between">
                        <span>Clouds ({(data.clouds || []).length})</span>
                        <CollapseBtn open={showClouds} onClick={() => setShowClouds(!showClouds)}
                            label={showClouds ? "Collapse" : `Expand (${(data.clouds || []).filter((e) => !isProblem(e.health)).length} healthy)`} />
                    </div>
                    {showClouds && <HealthTable title="" rows={data.clouds || []} testid="health-clouds" showLat={false} />}
                </div>
            )}

            {/* Alert history */}
            <div className="cm-card p-0 border border-zinc-800" data-testid="health-alerts">
                <div className="px-3 py-2 text-xs uppercase tracking-widest text-zinc-500 font-mono border-b border-zinc-800 flex items-center gap-2">
                    <AlertTriangle size={12} /> Alert history
                </div>
                {(alerts || []).length === 0 ? (
                    <div className="px-3 py-6 text-center text-zinc-600 text-xs">No alerts.</div>
                ) : (
                    <div className="divide-y divide-zinc-900 max-h-96 overflow-y-auto">
                        {(alerts || []).map((a) => (
                            a.event_type === "auto_repair" ? (
                                <div key={a._id || a.id} className="px-3 py-2 text-xs flex flex-wrap items-center gap-x-3 gap-y-1 font-mono">
                                    <span className="text-zinc-500">{fmtAgo(a.ts)}</span>
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border rounded-sm text-[11px] uppercase text-sky-300 border-sky-800/50 bg-sky-500/10"><Wrench size={10} /> Auto-Repair</span>
                                    <span className="text-zinc-300">{a.name}</span>
                                    <span className="text-zinc-600">{a.kind}</span>
                                    <span className="text-amber-300/80 uppercase text-[11px]">{a.action}</span>
                                    <span className="text-zinc-400">{a.detail}</span>
                                </div>
                            ) : (
                                <div key={a._id || a.id} className="px-3 py-2 text-xs flex flex-wrap items-center gap-x-3 gap-y-1 font-mono">
                                    <span className="text-zinc-500">{fmtAgo(a.ts)}</span>
                                    <span className="text-zinc-300">{a.name}</span>
                                    <span className="text-zinc-600">{a.kind}</span>
                                    <HealthBadge health={a.from_state} /> <span className="text-zinc-600">→</span> <HealthBadge health={a.to_state} />
                                </div>
                            )
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
